import type { UserServiceRepository } from './UserServiceRepository';
import type {
  PermissionValue,
  UpdatePermissionRequest,
  AddPermissionToRoleRequest,
  RolePermissionsResponse,
  SystemRoleData,
} from 'services/api/userService/types';
import type { PermissionConfig } from './permission-config';
import { PERMISSION_CONFIG } from './permission-config';
import { ApiError } from 'services/errors/ApiError';
import { trackError } from '@proposify/frontend-observability';
import {
  getCurrentUser,
  listUsers,
  listRoles,
  listPermissionDefinitions,
  listRolePermissions,
  updateRoleById,
  assignRoleToUser,
  requestVerification,
  listSystemUsersPermissions,
  markUsageStep,
  usageStepEnum,
} from '@proposify/users-bff-api-client';
import type {
  User,
  PermissionDefinition,
  ListUsersResponse,
  ListRolesQueryResponse,
  UpsertRole,
  AddRoleToUserDto,
} from '@proposify/users-bff-api-client';
import { listSystemUsersPermissionsQueryResponseSchema } from '@proposify/users-bff-api-client';
import { getUserServiceSchemaClientConfig } from 'services/api/client';
import { buildApproverConditions, parseApproverUserIds } from './approverConditions';

const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseMessage = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof responseMessage === 'string') return responseMessage;

  const message = (error as { message?: unknown })?.message;
  return typeof message === 'string' ? message : fallback;
};

const DOCUMENT_APPROVAL_PERMISSION_ID = PERMISSION_CONFIG.document_approval_required.key;

export class ApiUserServiceRepository implements UserServiceRepository {
  private pendingUpdates = new Map<string, Promise<PermissionValue>>();

  public async getAccountUsers(accountId: number): Promise<ListUsersResponse> {
    return await listUsers(accountId.toString(), getUserServiceSchemaClientConfig());
  }

  public async getCurrentUser(): Promise<User> {
    try {
      return await getCurrentUser(getUserServiceSchemaClientConfig());
    } catch (error) {
      trackError(error, { service: 'userService', operation: 'getCurrentUser' });
      throw new ApiError(getErrorMessage(error, 'Failed to get current user'), {
        cause: error instanceof Error ? error : undefined,
      });
    }
  }

  public async getUserRoles(accountId: number): Promise<ListRolesQueryResponse> {
    return await listRoles(accountId, getUserServiceSchemaClientConfig());
  }

  public async getPermissionDefinitions(): Promise<PermissionDefinition[]> {
    const response = await listPermissionDefinitions(getUserServiceSchemaClientConfig());
    return response.filter((def) => Boolean(def.permissionId) && Boolean(def.domainId));
  }

  public async getRolePermissions(accountId: string, roleId: string): Promise<RolePermissionsResponse> {
    const response = await listRolePermissions(Number(accountId), roleId, getUserServiceSchemaClientConfig());
    const permissions = Object.fromEntries(response.permissions.map((grant) => [grant.permissionId, grant.isGranted]));
    const approvalGrant = response.permissions.find((grant) => grant.permissionId === DOCUMENT_APPROVAL_PERMISSION_ID);
    return { roleId, permissions, documentApproverUserIds: parseApproverUserIds(approvalGrant?.conditions) };
  }

  public async updatePermission(request: UpdatePermissionRequest): Promise<PermissionValue> {
    const { roleId, payload } = request;
    const updateKey = `${roleId}-${payload.permissionId}`;
    const blockingUpdates = this.getBlockingUpdates(roleId, payload.permissionId, payload.enabled !== undefined);
    const updatePromise = this.executeUpdateWhenUnblocked(request, blockingUpdates);

    this.pendingUpdates.set(updateKey, updatePromise);

    try {
      return await updatePromise;
    } finally {
      this.clearPendingUpdate(updateKey, updatePromise);
    }
  }

  public async addPermissionToRole(request: AddPermissionToRoleRequest): Promise<void> {
    const { roleId, roleName, permissionId, domainId, enabled } = request;
    const body: UpsertRole = {
      name: roleName,
      permissions: [{ permissionId, domainId, isGranted: enabled }],
    };
    await updateRoleById(roleId, body, getUserServiceSchemaClientConfig());
  }

  public async updateUserWithRole(userId: string, data: AddRoleToUserDto): Promise<void> {
    await assignRoleToUser(userId, data, getUserServiceSchemaClientConfig());
  }

  public async requestVerification(userId: string): Promise<void> {
    try {
      await requestVerification(userId, getUserServiceSchemaClientConfig());
    } catch (error) {
      trackError(error, { service: 'userService', operation: 'requestVerification', userId });
      throw new ApiError(getErrorMessage(error, 'Failed to request email verification'), {
        cause: error instanceof Error ? error : undefined,
      });
    }
  }

  public async markGettingStartedVisited(userId: string): Promise<void> {
    try {
      await markUsageStep(userId, usageStepEnum.getting_started_visited, getUserServiceSchemaClientConfig());
    } catch (error) {
      trackError(error, { service: 'userService', operation: 'markGettingStartedVisited', userId });
      throw new ApiError(getErrorMessage(error, 'Failed to mark getting started visited'), {
        cause: error instanceof Error ? error : undefined,
      });
    }
  }

  public async getSystemRoles(): Promise<SystemRoleData[]> {
    const response = listSystemUsersPermissionsQueryResponseSchema.parse(
      await listSystemUsersPermissions(getUserServiceSchemaClientConfig())
    );
    return response.map((r) => ({
      id: r.id,
      name: r.name,
      permissions: r.permissions.reduce<Record<string, PermissionValue>>((acc, grant) => {
        acc[grant.permission_definition_id] = grant.is_granted;
        return acc;
      }, {}),
    }));
  }

  private async executeUpdate(request: UpdatePermissionRequest): Promise<PermissionValue> {
    const { roleId, roleName, payload, allPermissions, definitions, documentApproverUserIds } = request;

    if (payload.enabled === undefined) {
      throw new Error('Invalid payload: toggle updates must provide enabled');
    }

    let cascaded = allPermissions;
    if (payload.enabled === false) {
      cascaded = { ...allPermissions };
      this.disableChildrenInData(payload.permissionId, cascaded);
    }

    const grants = definitions.map((def) => {
      const isGranted = Boolean(cascaded[def.permissionId]);
      return {
        permissionId: def.permissionId,
        domainId: def.domainId,
        isGranted,
        // Only attach conditions while granted — matches the backend's documented "isGranted: false
        // clears conditions" contract, and avoids leaving stale approvers on a disabled grant.
        ...(def.permissionId === DOCUMENT_APPROVAL_PERMISSION_ID && isGranted
          ? { conditions: buildApproverConditions(documentApproverUserIds ?? []) }
          : {}),
      };
    });

    const body: UpsertRole = { name: roleName, permissions: grants };
    await updateRoleById(roleId, body, getUserServiceSchemaClientConfig());

    return payload.enabled;
  }

  private getBlockingUpdates(roleId: string, permissionId: string, includeDescendants: boolean): Promise<PermissionValue>[] {
    const permissionKeys = includeDescendants ? [permissionId, ...this.getAllDescendants(permissionId)] : [permissionId];

    return permissionKeys
      .map((key) => this.pendingUpdates.get(`${roleId}-${key}`))
      .filter((update): update is Promise<PermissionValue> => Boolean(update));
  }

  private async executeUpdateWhenUnblocked(
    request: UpdatePermissionRequest,
    blockingUpdates: Promise<PermissionValue>[]
  ): Promise<PermissionValue> {
    if (blockingUpdates.length > 0) {
      await Promise.all(blockingUpdates);
    }

    return this.executeUpdate(request);
  }

  private clearPendingUpdate(updateKey: string, updatePromise: Promise<PermissionValue>): void {
    if (this.pendingUpdates.get(updateKey) === updatePromise) {
      this.pendingUpdates.delete(updateKey);
    }
  }

  private findConfigByKey(configObj: Record<string, PermissionConfig>, key: string): PermissionConfig | null {
    for (const config of Object.values(configObj)) {
      if (config.key === key) return config;
      if (config.children) {
        const found = this.findConfigByKey(config.children, key);
        if (found) return found;
      }
    }
    return null;
  }

  private getAllDescendants(permissionKey: string): string[] {
    const descendants: string[] = [];
    const config = this.findConfigByKey(PERMISSION_CONFIG, permissionKey);

    if (!config?.children) return descendants;

    const traverse = (children: Record<string, PermissionConfig>) => {
      for (const child of Object.values(children)) {
        descendants.push(child.key);
        if (child.children) {
          traverse(child.children);
        }
      }
    };

    traverse(config.children);
    return descendants;
  }

  private disableChildrenInData(parentKey: string, permissionData: Record<string, PermissionValue>): void {
    const parentConfig = this.findConfigByKey(PERMISSION_CONFIG, parentKey);
    if (!parentConfig?.children) return;

    for (const childConfig of Object.values(parentConfig.children)) {
      if (childConfig.key in permissionData) {
        permissionData[childConfig.key] = false;

        if (childConfig.children) {
          this.disableChildrenInData(childConfig.key, permissionData);
        }
      }
    }
  }
}

// Singleton instance shared across all hooks
export const userServiceRepository = new ApiUserServiceRepository();
